/**
 * Copy for the repository-import answers the API refuses with an `errorKey`
 * (App/Resource/Git/import.ts), translated here rather than shown as the
 * server's English sentence (R92-L9); anything else falls back to the
 * server's sentence, then to a generic one.
 */
type Translate = (
  key: string,
  values?: Record<string, string>,
  opts?: { defaultValue: string },
) => string

const IMPORT_ERROR_COPY: Record<string, { key: string; defaultValue: string }> = {
  'git.error.uploadUnavailable': {
    key: 'importRepo.error.uploadUnavailable',
    defaultValue: 'File upload is not available here — connect a repository instead.',
  },
  'git.error.uploadNotGzip': {
    key: 'importRepo.error.notGzip',
    defaultValue:
      'That file is not a .tar.gz archive. Compress your project with tar and try again.',
  },
  'git.error.repoTooLarge': {
    key: 'importRepo.error.tooLarge',
    defaultValue: 'That repository is too large for your plan’s import limit.',
  },
  'git.error.importLimit': {
    key: 'importRepo.error.limit',
    defaultValue: 'At most five repositories can be imported into one project.',
  },
  'git.error.importAfterBoot': {
    key: 'importRepo.error.afterBoot',
    defaultValue:
      'This project has already started. Repositories can be imported only before its first build.',
  },
}

/**
 * The sentence to show for a failed import request.
 *
 * @param err - The HTTP error (its response body, when any).
 * @param t - Translate.
 * @returns The sentence.
 */
export function importErrorMessage(
  err: { response?: { data?: { errorKey?: unknown; error?: unknown } } } | null | undefined,
  t: Translate,
): string {
  const data = err?.response?.data
  const key = typeof data?.errorKey === 'string' ? data.errorKey : null
  const known = key ? IMPORT_ERROR_COPY[key] : undefined
  if (known) return t(known.key, undefined, { defaultValue: known.defaultValue })
  if (typeof data?.error === 'string' && data.error) return data.error
  return t('importRepo.error.saveFailed', undefined, {
    defaultValue: 'Could not select that repository. Try again.',
  })
}

/** The gzip magic number: every `.tar.gz` starts with these two bytes. */
const GZIP_MAGIC = [0x1f, 0x8b] as const

/**
 * Whether a file begins with the gzip magic number — checked before the upload
 * leaves the browser (the server refuses anything else with 415, R92-L9).
 *
 * @param file - The chosen file.
 * @returns True when it looks like gzip.
 */
export async function looksLikeGzip(file: Blob): Promise<boolean> {
  if (file.size < 2) return false
  const head = new Uint8Array(await file.slice(0, 2).arrayBuffer())
  return head[0] === GZIP_MAGIC[0] && head[1] === GZIP_MAGIC[1]
}
